import type { ApiEndpointKeyType } from './api-endpoint-key';
import { ApiEndpointKey } from './api-endpoint-key';
import type { BdAppConfigRequest, BdProtectedResource } from './app-config';
import { META_BACKEND_PROTECTED_PATH_ROOTS } from './meta-backend-protected-path-roots';

/** Which Modulith host serves a hosted endpoint. Only DE exists today; AT/CH follow the same `<origin>/api` layout later. */
export type BdBackendHost = 'DE' | 'AT' | 'CH';

/** Env var that carries each host's origin (no path, e.g. https://meta-backend-api.bindoc-baut.de). */
export const META_BACKEND_API_URL_KEY: Readonly<Record<BdBackendHost, string>> = {
  DE: 'META_BACKEND_API_URL',
  AT: 'META_BACKEND_API_AT_URL',
  CH: 'META_BACKEND_API_CH_URL',
};

export interface HostedApiEndpoint {
  host: BdBackendHost;
  /** Absolute path on the host, `/api[/<suffix>]` — the host has one URL space under `/api` (BDA-6595). */
  path: string;
}

/**
 * Every folded module answers on the host's own URL space, `/api/...`, with the path the retired
 * deployable served under its subdomain. Keys that share a base URL share the value on purpose;
 * the MSAL map protects the host's roots from `META_BACKEND_PROTECTED_PATH_ROOTS`, not a prefix
 * per module (BDA-6595, see `hostProtectedResources`).
 */
export const HOSTED_API_ENDPOINT: Readonly<Partial<Record<ApiEndpointKeyType, HostedApiEndpoint>>> = {
  [ApiEndpointKey.BD_PERMISSION_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_MARKET_STORAGE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_CODE_SPECTRUM_STORAGE_URL]: { host: 'DE', path: '/api/DE/code-spectra' },
  [ApiEndpointKey.AT_CODE_SPECTRUM_API_URL]: { host: 'DE', path: '/api/AT/code-spectra' },
  [ApiEndpointKey.CH_CODE_SPECTRUM_API_URL]: { host: 'DE', path: '/api/CH/code-spectra' },
  [ApiEndpointKey.UK_CODE_SPECTRUM_API_URL]: { host: 'DE', path: '/api/UK/code-spectra' },
  [ApiEndpointKey.BD_BUSINESS_CASE_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_CLINIC_FILTER_STORAGE_URL]: { host: 'DE', path: '/api/clinics-filters' },
  [ApiEndpointKey.AYS_CLINIC_RESOURCE_URL]: { host: 'DE', path: '/api/clinics' },
  [ApiEndpointKey.AYS_CLINIC_EXPORT_URL]: { host: 'DE', path: '/api/clinics/exports' },
  [ApiEndpointKey.AYS_CLINIC_ANNUAL_ACCOUNT_URL]: { host: 'DE', path: '/api/clinics/*/annual-account' },
  [ApiEndpointKey.AYS_CLINICS_CUBE_URL]: { host: 'DE', path: '/api/kennzahlen-benchmarking' },
  [ApiEndpointKey.AYS_COMPETITORS_ANALYSIS_RESOURCE_URL]: { host: 'DE', path: '/api/competitor-analysis' },
  [ApiEndpointKey.AYS_COMPETITORS_ANALYSIS_CALCULATION_URL]: { host: 'DE', path: '/api/competitors' },
  [ApiEndpointKey.AYS_FAB_RESOURCE_URL]: { host: 'DE', path: '/api/specialty-departments/fabs' },
  [ApiEndpointKey.BD_FILES_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.BD_JOBS_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_CODE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.BD_DISTRICT_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_PROJECTION_RESOURCE_URL]: { host: 'DE', path: '/api/projection' },
  [ApiEndpointKey.DRG_PROFIT_LOSS_ANALYSIS_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_SCENARIO_ANALYSIS_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_POTENTIAL_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.CUBE_DASHBOARD_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_DEMAND_ANALYSIS_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_VIRTUAL_CLINIC_RESOURCE_URL]: { host: 'DE', path: '/api' },
  [ApiEndpointKey.AYS_BENCHMARKING_GROUP_RESOURCE_URL]: { host: 'DE', path: '/api/benchmarking-groups' },
  [ApiEndpointKey.BD_AI_API_URL]: { host: 'DE', path: '/api' },
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * The origin of every Modulith host whose env var is set (`META_BACKEND_API_URL`, …), without a
 * trailing slash. Empty when no host var is set.
 */
export function backendHostOrigins(source: Record<string, unknown>): Partial<Record<BdBackendHost, string>> {
  const origins: Partial<Record<BdBackendHost, string>> = {};

  for (const host of Object.keys(META_BACKEND_API_URL_KEY) as BdBackendHost[]) {
    const origin = source[META_BACKEND_API_URL_KEY[host]];
    if (isNonEmptyString(origin)) {
      origins[host] = origin.replace(/\/+$/, '');
    }
  }

  return origins;
}

/**
 * The MSAL resources for the protected endpoint keys (BDA-6595).
 *
 * `protectedResources` is a positive list: what is in it gets a token or a login redirect,
 * everything else goes out anonymous. A key whose URL is the bare base of a Modulith host
 * (`<origin>/api`) would protect the whole host, public clinic pages included. Such a key is
 * replaced by one entry per protected path root of that host (`roots`), emitted once however
 * many keys share the base. A key with a path of its own (`…/api/clinics/exports`,
 * `…/api/DE/code-spectra`) stays an entry of its own, as before. Without a host var nothing
 * changes: every key keeps its entry.
 */
export function hostProtectedResources(
  apiEndpoints: Readonly<Record<string, string>>,
  scopes: string[],
  source: Record<string, unknown>,
  roots: Readonly<Record<BdBackendHost, readonly string[]>> = META_BACKEND_PROTECTED_PATH_ROOTS,
): BdProtectedResource[] {
  const hostByBase = new Map<string, BdBackendHost>();
  for (const [host, origin] of Object.entries(backendHostOrigins(source)) as [BdBackendHost, string][]) {
    hostByBase.set(`${origin}/api`, host);
  }

  const uris: string[] = [];
  const hostsOnBareBase = new Set<BdBackendHost>();
  for (const uri of Object.values(apiEndpoints)) {
    const host = typeof uri === 'string' ? hostByBase.get(uri.replace(/\/+$/, '')) : undefined;
    if (host) {
      hostsOnBareBase.add(host);
    } else {
      uris.push(uri);
    }
  }
  for (const [base, host] of hostByBase) {
    if (hostsOnBareBase.has(host)) {
      uris.push(...roots[host].map((root) => `${base}${root}`));
    }
  }

  return [...new Set(uris)].map((uri) => ({ uri, scopes }));
}

/**
 * Returns a copy of `source` in which every key of `request.hostedApiEndpointKeys` without an
 * explicit value is derived as `<host origin env var>` + `table[key].path`.
 * An explicit, non-empty env var for the key always wins. Never mutates `source` (may be `process.env`).
 */
export function expandHostedEndpoints(
  request: Pick<BdAppConfigRequest, 'apiEndpointKeys' | 'unprotectedApiEndpointKeys' | 'hostedApiEndpointKeys'>,
  source: Record<string, unknown>,
  table: Readonly<Partial<Record<ApiEndpointKeyType, HostedApiEndpoint>>> = HOSTED_API_ENDPOINT,
): Record<string, unknown> {
  const requestedKeys = new Set<ApiEndpointKeyType>([
    ...request.apiEndpointKeys,
    ...(request.unprotectedApiEndpointKeys ?? []),
  ]);
  const derived: Record<string, string> = {};

  for (const key of request.hostedApiEndpointKeys ?? []) {
    if (!requestedKeys.has(key)) {
      throw new Error(`hostedApiEndpointKeys: ${key} is not a requested endpoint key`);
    }

    const entry = table[key];
    if (!entry) {
      throw new Error(`hostedApiEndpointKeys: ${key} has no hosted path (HOSTED_API_ENDPOINT)`);
    }

    if (isNonEmptyString(source[key])) {
      continue;
    }

    const hostVar = META_BACKEND_API_URL_KEY[entry.host];
    const origin = source[hostVar];
    if (!isNonEmptyString(origin)) {
      // Dormant until the deployment sets the host var: the key stays exactly
      // as the source has it (possibly unset, which security-lib tolerates),
      // so an image with hosted keys never crashes a pod whose environment
      // has not switched yet (QA/production switch one at a time, BDA-6557).
      continue;
    }

    derived[key] = origin.replace(/\/+$/, '') + entry.path;
  }

  return { ...source, ...derived };
}
